/**
 * Agent Login Page
 * Allows agents to sign in using their Slack account
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Avatar
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Slack brand colors
const slackColors = {
  aubergine: '#4A154B',
  blue: '#36C5F0',
  green: '#2EB67D',
  yellow: '#ECB22E',
  red: '#E01E5A'
};

const AgentLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check for existing session on load
  useEffect(() => {
    const checkSession = async () => {
      const token = localStorage.getItem('agentToken');
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/call-centre/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            // Valid session, redirect to dashboard
            const redirect = new URLSearchParams(location.search).get('redirect') || '/call-centre';
            navigate(redirect);
            return;
          }
        } catch (err) {
          // Invalid token, clear it
          localStorage.removeItem('agentToken');
          localStorage.removeItem('agentInfo');
        }
      }
      setCheckingSession(false);
    };

    checkSession();
  }, [navigate, location]);

  const handleSlackLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const redirect = new URLSearchParams(location.search).get('redirect') || '/call-centre';
      const response = await axios.get(`${API_URL}/api/call-centre/auth/slack/start?redirect=${encodeURIComponent(redirect)}`);

      if (response.data.success && response.data.authUrl) {
        // Redirect to Slack OAuth
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to start Slack authentication');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to connect to Slack');
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #4F46E5 0%, var(--color-primary) 50%, var(--color-accent) 100%)'
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #4F46E5 0%, var(--color-primary) 50%, var(--color-accent) 100%)',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3
          }}
        >
          {/* Logo/Header */}
          <Box sx={{ mb: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto',
                background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
                fontSize: '2rem'
              }}
            >
              SC
            </Avatar>
            <Typography variant="h4" sx={{ mt: 2, fontWeight: 700, color: '#263238' }}>
              Call Centre
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              SuperChargeMe Agent Portal
            </Typography>
          </Box>

          {/* Info */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in with your company Slack account to access the call centre dashboard.
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Slack Login Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSlackLogin}
            disabled={loading}
            sx={{
              py: 1.5,
              backgroundColor: slackColors.aubergine,
              '&:hover': {
                backgroundColor: '#611f69'
              },
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Slack Logo */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#fff"/>
                </svg>
                <span>Sign in with Slack</span>
              </Stack>
            )}
          </Button>

          {/* Help Text */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              Having trouble signing in?{' '}
              <a href="mailto:support@superchargeme.com" style={{ color: 'var(--color-primary)' }}>
                Contact support
              </a>
            </Typography>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 3, color: 'rgba(255,255,255,0.7)' }}
        >
          SuperChargeMe Data Platform &copy; {new Date().getFullYear()}
        </Typography>
      </Container>
    </Box>
  );
};

export default AgentLogin;
